import React from 'react';
import { addDays, addHours, format, isAfter, subMinutes } from 'date-fns';
import { InputField, SelectFieldCharterPricing } from 'components/Global';

import SelectFieldTime from 'components/Global/SelectFieldTime';
import { ReactComponent as Calendar } from 'icons/calendar-icon.svg';
import { ReactComponent as Clock } from 'icons/clock-icon.svg';
import { id } from 'date-fns/locale';
import { useEffect } from 'react';
import { useAppContext } from 'components/Context/AppContext';
import { isSelectedTimeValid } from 'utils/functionality';

const addHoursToDateTime = (date, time, hours) => {
  const [hour, minute] = time.split(':').map(Number);

  const dateTime = new Date(date);
  dateTime.setHours(hour, minute, 0, 0);

  const newDateTime = addHours(dateTime, hours);

  return format(newDateTime, 'HH:mm');
};

const FormComponent = ({
  item,
  index,
  startDate,
  disable,
  allRentTime,
  setAllRentTime,
  charterPricingData,
  disabledTimes,
  staticDisabledTime,
}) => {
  const { showToast } = useAppContext();

  const selectCharterPricing = (price) => {
    if (allRentTime?.length === 0) return;

    let copyAllRentTime = [...allRentTime];

    const startRentDate = format(startDate, 'yyyy-MM-dd');
    const startTime = copyAllRentTime[index]?.booking_start_time;

    if (!startTime) {
      copyAllRentTime[index] = {
        ...copyAllRentTime[index],
        charter_duration: price,
        booking_end_time: '',
      };
      setAllRentTime(copyAllRentTime);
      return;
    }

    let endTime = addHoursToDateTime(addDays(startDate, index), startTime, price.duration);

    if (endTime === '00:00') {
      endTime = format(subMinutes(new Date(`${startRentDate} ${endTime}`), 1), 'HH:mm');
    }

    copyAllRentTime[index] = {
      ...copyAllRentTime[index],
      charter_duration: price,
      booking_end_time: endTime,
    };
    setAllRentTime(copyAllRentTime);
  };

  const selectTimeHandler = (hour, minute) => {
    if (allRentTime?.length === 0) return;

    let copyAllRentTime = [...allRentTime];
    const duration = copyAllRentTime[index]?.charter_duration?.duration || 4;

    const startRentDate = format(startDate, 'yyyy-MM-dd');
    const startTime = `${hour}:${minute ? minute : '00'}`;

    const endTimeHour = addHours(new Date(`${startRentDate}T${startTime}`), duration);
    const endTimeMinute = minute ? minute : '00';
    let endTime = `${format(endTimeHour, 'HH')}:${endTimeMinute}`;

    if (endTime === '00:00') {
      endTime = format(subMinutes(new Date(`${startRentDate}T${endTime}`), 1), 'HH:mm');
    }

    let charterDuration = copyAllRentTime[index]?.charter_duration;
    const limitDate = addHours(startDate, 24);

    if (isAfter(endTimeHour, limitDate)) {
      charterDuration = undefined;
      const newEndTimeHour = addHours(new Date(`${startRentDate}T${startTime}`), 4);
      endTime = `${format(newEndTimeHour, 'HH')}:${endTimeMinute}`;
      showToast({ type: 'error', message: 'Waktu tidak tersedia.' });
    }

    if (
      !isSelectedTimeValid(
        hour,
        minute,
        format(startDate, 'yyyy-MM-dd'),
        disabledTimes?.disabledTimes,
        staticDisabledTime,
      )
    ) {
      showToast({ type: 'error', message: 'Waktu tidak tersedia.' });
      return;
    }

    copyAllRentTime[index] = {
      ...copyAllRentTime[index],
      booking_start_time: startTime,
      booking_end_time: endTime,
      charter_duration: charterDuration,
    };
    setAllRentTime(copyAllRentTime);
  };

  return (
    <div key={item} className="rent-time-components__time">
      <InputField
        label={`Hari ke-${item}`}
        icon={<Calendar />}
        iconPosition={'start'}
        disable
        value={format(addDays(startDate, index), 'dd MMMM yyyy', { locale: id })}
      />

      <SelectFieldCharterPricing
        label="Durasi Sewa"
        id={`charter-pricing-${item}`}
        placeholder="Pilih Durasi Sewa"
        data={charterPricingData}
        selectedPrice={allRentTime[index]?.charter_duration}
        onSelectPrice={selectCharterPricing}
        disable={disable || !charterPricingData || charterPricingData.length === 0}
        startDate={startDate}
        starHour={allRentTime[index]?.booking_start_time}
      />

      <SelectFieldTime
        label="Start Time"
        htmlFor={`jam-mulai-${item}`}
        placeholder="00:00"
        disable={disable}
        value={allRentTime[index]?.booking_start_time}
        onChange={selectTimeHandler}
        disabledTimes={disabledTimes}
        initialHour="00"
        showAllHours
        staticDisabledTime={staticDisabledTime}
        startHour={format(startDate, 'yyyy-MM-dd')}
      />

      <InputField
        label="End Time"
        htmlFor={`jam-selesai-${item}`}
        icon={<Clock />}
        iconPosition={'start'}
        disable
        placeholder={'00:00'}
        value={allRentTime[index]?.booking_end_time}
        readOnly
      />
    </div>
  );
};

const RentTimeComponents = ({
  durationRent,
  startDate,
  disable,
  allRentTime,
  setAllRentTime,
  charterPricingData,
  disabledTimes,
  staticDisabledTime,
}) => {
  useEffect(() => {
    if (!durationRent || allRentTime?.length !== 0) return;

    let arrayRentTime = [];

    for (let i = 0; i < durationRent; i++) {
      arrayRentTime.push({
        charter_duration: undefined,
        booking_start_time: '',
        booking_end_time: '',
      });
    }

    setAllRentTime(arrayRentTime);
  }, [durationRent]);

  return (
    <>
      {durationRent !== 0 && (
        <div className="rent-time-components">
          {Array.from({ length: durationRent }, (_, i) => i + 1).map((item, index) => (
            <FormComponent
              key={item}
              item={item}
              index={index}
              startDate={startDate}
              disable={disable}
              allRentTime={allRentTime}
              setAllRentTime={setAllRentTime}
              charterPricingData={charterPricingData}
              disabledTimes={disabledTimes}
              staticDisabledTime={staticDisabledTime}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default RentTimeComponents;
